// ---------------------------------------------------------------------------
// Pricing data layer (OPUS-D20)
// ---------------------------------------------------------------------------
// TanStack Query v5 hook + fetcher for the public pricing-page read model.
// Contract mirrors apps/api/src/routes/pricing-page.ts (feature #53) and the
// three-tier shape (feature #17: Starter / Agency / Enterprise).
//
// The API returns whatever tiers are currently active. If the backend is cold
// or the feature flag is off we fall back to a deterministic, locally-declared
// default deck so the page always renders the three canonical tiers. This
// keeps the marketing experience stable while the backend seed lands.
// ---------------------------------------------------------------------------

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

// ---- domain types --------------------------------------------------------

/**
 * Canonical tier codes for the three-tier plan. Matches the
 * `three-tier-pricing` feature (#17) vocabulary.
 */
export const TIER_CODES = ["starter", "agency", "enterprise"] as const;
export type TierCode = (typeof TIER_CODES)[number];

/**
 * Shape returned by GET /api/v1/pricing-page/tiers. Prices are USD minor
 * units (cents). `tier_code` is a free-form slug server-side; the UI
 * prefers the canonical Starter/Agency/Enterprise trio but tolerates
 * whatever the backend publishes.
 */
export interface PublicTier {
	tier_code: string;
	name: string;
	monthly_price_usd: number;
	included_conversions: number;
	overage_per_conversion_usd: number;
	feature_highlights: string[];
	cta_label: string;
}

/**
 * UI view-model extension. `kind` decides price presentation — "fixed" shows
 * a dollar amount, "custom" shows "Custom quote". Agency and Enterprise are
 * both custom-quoted per the sales motion; only Starter is self-serve.
 */
export type TierKind = "fixed" | "custom";

export interface PricingTierView {
	tier_code: string;
	name: string;
	kind: TierKind;
	monthly_price_usd: number;
	included_conversions: number;
	overage_per_conversion_usd: number;
	feature_highlights: string[];
	cta_label: string;
	tagline: string;
	/** Ordering within the card grid (starter → agency → enterprise). */
	sort_rank: number;
}

// ---- base url resolution -------------------------------------------------

/**
 * Resolves the api base url for the dashboard. In local dev the dash is on
 * :5173 and the api on :8787; in prod they share origin. Mirrors opD04 so
 * both routes use the same heuristic.
 */
export function resolveApiBase(): string {
	const envBase =
		typeof import.meta !== "undefined" &&
		(import.meta as unknown as { env?: Record<string, string> }).env?.[
			"VITE_API_BASE"
		];
	if (envBase && envBase.length > 0) return envBase.replace(/\/$/, "");
	if (typeof window !== "undefined") {
		const { hostname, protocol } = window.location;
		if (hostname === "localhost" || hostname === "127.0.0.1") {
			return `${protocol}//${hostname}:8787`;
		}
	}
	return "";
}

/**
 * Resolves the tenant's current plan. Sourced from `VITE_TENANT_PLAN` so
 * the dash can render a "Current plan" badge without wiring the auth
 * middleware yet. Defaults to "starter" — the free-trial / self-serve
 * default assumed by feature #21 (free-trial).
 */
export function resolveCurrentPlan(): TierCode {
	const raw =
		typeof import.meta !== "undefined" &&
		(import.meta as unknown as { env?: Record<string, string> }).env?.[
			"VITE_TENANT_PLAN"
		];
	const normalized = typeof raw === "string" ? raw.toLowerCase().trim() : "";
	if ((TIER_CODES as readonly string[]).includes(normalized)) {
		return normalized as TierCode;
	}
	return "starter";
}

// ---- fetcher -------------------------------------------------------------

export async function fetchPublicTiers(
	signal?: AbortSignal,
): Promise<PublicTier[]> {
	const base = resolveApiBase();
	const url = `${base}/api/v1/pricing-page/tiers`;
	const res = await fetch(url, {
		method: "GET",
		signal,
		headers: { Accept: "application/json" },
		credentials: "include",
	});
	if (!res.ok) {
		const text = await res.text().catch(() => "");
		throw new Error(
			`pricing.tiers failed (${res.status}): ${text || res.statusText}`,
		);
	}
	const json = (await res.json()) as unknown;
	if (!Array.isArray(json)) return [];
	// Defensive shape-check — we do not trust drift between deploys.
	return json
		.map((raw): PublicTier | null => {
			if (!raw || typeof raw !== "object") return null;
			const r = raw as Record<string, unknown>;
			if (typeof r.tier_code !== "string") return null;
			if (typeof r.name !== "string") return null;
			if (typeof r.monthly_price_usd !== "number") return null;
			return {
				tier_code: r.tier_code,
				name: r.name,
				monthly_price_usd: r.monthly_price_usd,
				included_conversions:
					typeof r.included_conversions === "number"
						? r.included_conversions
						: 0,
				overage_per_conversion_usd:
					typeof r.overage_per_conversion_usd === "number"
						? r.overage_per_conversion_usd
						: 0,
				feature_highlights: Array.isArray(r.feature_highlights)
					? r.feature_highlights.filter(
							(x): x is string => typeof x === "string",
						)
					: [],
				cta_label:
					typeof r.cta_label === "string" ? r.cta_label : "Choose plan",
			};
		})
		.filter((t): t is PublicTier => t !== null);
}

// ---- default deck --------------------------------------------------------

/**
 * Local defaults for the three canonical tiers. Used when the backend has no
 * seed yet (first boot, feature-flag off, or GET /tiers returns an empty
 * list). Values mirror feature #17 marketing intent:
 *   - Starter  $299 / mo, self-serve entry plan
 *   - Agency   custom quote, volume + reseller
 *   - Enterprise custom quote, SSO + SLA + DPA
 */
const DEFAULT_TIERS: Record<TierCode, PricingTierView> = {
	starter: {
		tier_code: "starter",
		name: "Starter",
		kind: "fixed",
		monthly_price_usd: 29_900, // $299 in cents
		included_conversions: 50_000,
		overage_per_conversion_usd: 2, // $0.02 / conversion
		feature_highlights: [
			"50,000 conversions / month included",
			"Unlimited clicks and impressions",
			"Standard attribution and postbacks",
			"Self-serve setup with email support",
		],
		cta_label: "Start trial",
		tagline: "Self-serve plan for teams getting off the ground.",
		sort_rank: 0,
	},
	agency: {
		tier_code: "agency",
		name: "Agency",
		kind: "custom",
		monthly_price_usd: 0,
		included_conversions: 0,
		overage_per_conversion_usd: 0,
		feature_highlights: [
			"Custom volume pricing on conversions",
			"Multi-tenant agency console and sub-networks",
			"White-label branding and custom domains",
			"Priority chat support with named CSM",
		],
		cta_label: "Request quote",
		tagline: "For performance agencies scaling many brands at once.",
		sort_rank: 1,
	},
	enterprise: {
		tier_code: "enterprise",
		name: "Enterprise",
		kind: "custom",
		monthly_price_usd: 0,
		included_conversions: 0,
		overage_per_conversion_usd: 0,
		feature_highlights: [
			"Volume commitment with committed pricing",
			"SSO, SCIM, SOC 2 and signed DPA",
			"Dedicated data residency region",
			"99.95% uptime SLA with 24h response",
		],
		cta_label: "Contact sales",
		tagline: "Regulated scale with security, residency, and SLA controls.",
		sort_rank: 2,
	},
};

/**
 * Merges API-published tiers with the canonical default deck. The three
 * canonical codes always render; API data wins whenever it's present.
 * Unknown tier_codes from the API are ignored by the UI (they would break
 * the 3-column grid) but surfaced via the returned list for future use.
 */
export function buildTierDeck(apiTiers: PublicTier[]): PricingTierView[] {
	const byCode = new Map<string, PublicTier>();
	for (const t of apiTiers) byCode.set(t.tier_code, t);

	return TIER_CODES.map<PricingTierView>((code) => {
		const base = DEFAULT_TIERS[code];
		const api = byCode.get(code);
		if (!api) return base;
		return {
			...base,
			name: api.name || base.name,
			// The API tier is a fixed price if it publishes a non-zero
			// monthly_price_usd — custom-quote tiers store 0.
			kind: api.monthly_price_usd > 0 ? "fixed" : base.kind,
			monthly_price_usd:
				api.monthly_price_usd > 0
					? api.monthly_price_usd
					: base.monthly_price_usd,
			included_conversions:
				api.included_conversions > 0
					? api.included_conversions
					: base.included_conversions,
			overage_per_conversion_usd:
				api.overage_per_conversion_usd > 0
					? api.overage_per_conversion_usd
					: base.overage_per_conversion_usd,
			feature_highlights:
				api.feature_highlights.length > 0
					? api.feature_highlights
					: base.feature_highlights,
			cta_label: api.cta_label || base.cta_label,
		};
	});
}

// ---- react-query hook ----------------------------------------------------

export const PRICING_TIERS_QUERY_KEY = ["pricing", "tiers"] as const;

export function usePricingTiers(): UseQueryResult<PricingTierView[], Error> {
	return useQuery<PricingTierView[], Error>({
		queryKey: PRICING_TIERS_QUERY_KEY,
		queryFn: async ({ signal }) => {
			// Even if the API round-trip fails we still render the default
			// deck so prospects never see a blank pricing page. Errors bubble
			// up via isError so the UI can show a muted retry banner.
			const api = await fetchPublicTiers(signal);
			return buildTierDeck(api);
		},
		// The canonical deck is always ready — treat it as fresh for a minute
		// so tab-switching doesn't spam the api.
		staleTime: 60_000,
		refetchOnWindowFocus: false,
		retry: 1,
		// Seed with the canonical default deck so the UI has something to
		// paint on first render even before the network resolves.
		initialData: () => buildTierDeck([]),
	});
}

// ---- formatting helpers --------------------------------------------------

/**
 * Formats a USD minor-unit price as `$299` (or `$1,200`). Strips trailing
 * `.00` since our tiers are whole-dollar. Defensive against NaN.
 */
export function formatUsdMinor(cents: number): string {
	if (!Number.isFinite(cents) || cents <= 0) return "$0";
	const dollars = Math.round(cents / 100);
	return `$${new Intl.NumberFormat("en-US").format(dollars)}`;
}

/**
 * Formats included_conversions as `50,000` or `1M` for compactness at
 * large volumes.
 */
export function formatIncluded(n: number): string {
	if (!Number.isFinite(n) || n <= 0) return "0";
	if (n >= 1_000_000) {
		const m = n / 1_000_000;
		return `${m.toFixed(m < 10 ? 1 : 0).replace(/\.0$/, "")}M`;
	}
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}
